import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import { GalleryItem } from '../../types.ts';
import {
  Youtube,
  Image as ImageIcon,
  Play,
  X,
  Eye,
  Maximize2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Layers,
  Touchpad,
  Film,
  Video,
} from 'lucide-react';
import {
  isVideoItem,
  isDirectVideoUrl,
  isIframeEmbed,
  extractIframeSrc,
  getYouTubeId,
  getYouTubeThumbnail,
  getYouTubeEmbedUrl,
  getVimeoId,
} from '../../utils/videoUtils.ts';

interface GalleryVideoWidgetProps {
  badge?: string;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  buttonEnabled?: boolean;
  limit?: number;
  isPageMode?: boolean;
}

export default function GalleryVideoWidget({
  badge = 'VISUAL DOCUMENTATION & MEDIA',
  title = 'Media Showcase & Video Archives',
  subtitle = 'Capturing clean technology deployments, environmental symposiums, and green innovation projects',
  buttonText = 'View Full Media & Video →',
  buttonEnabled = true,
  limit = 8,
  isPageMode = false,
}: GalleryVideoWidgetProps) {
  const { gallery, settings, navigate } = useApp();
  const [activeFilter, setActiveFilter] = useState<'all' | 'photo' | 'video'>('all');
  const [visibleCount, setVisibleCount] = useState<number>(limit || 8);
  const [lightboxItem, setLightboxItem] = useState<GalleryItem | null>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  // Touch Swipe Engine for Mobile Gestures
  const touchStartXRef = useRef<number>(0);
  const touchStartYRef = useRef<number>(0);
  const touchEndXRef = useRef<number>(0);
  const touchEndYRef = useRef<number>(0);
  const [touchSwiping, setTouchSwiping] = useState(false);
  const [touchDeltaX, setTouchDeltaX] = useState(0);

  const primaryColor = settings?.theme.primaryColor || '#059669';

  const handleOpenLightbox = (item: GalleryItem, initialIndex = 0) => {
    setLightboxItem(item);
    setCurrentSlideIndex(initialIndex);
  };

  const handleCloseLightbox = () => {
    setLightboxItem(null);
    setCurrentSlideIndex(0);
    setTouchDeltaX(0);
  };

  const filtered = gallery.filter((item) => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'photo') return item.type === 'photo';
    if (activeFilter === 'video') return isVideoItem(item);
    return true;
  });
  const visibleGallery = isPageMode ? filtered.slice(0, visibleCount) : filtered.slice(0, limit);

  // Active item & subphotos in lightbox
  const isCurrentItemVideo = lightboxItem ? isVideoItem(lightboxItem) : false;
  const activeItemIndex = lightboxItem ? filtered.findIndex((it) => it.id === lightboxItem.id) : -1;
  const currentSubPhotos =
    lightboxItem && !isCurrentItemVideo && lightboxItem.type === 'photo'
      ? [lightboxItem.url, ...(lightboxItem.gallery || [])].filter(Boolean)
      : [];

  const hasMultipleInCurrentItem = currentSubPhotos.length > 1;
  const canNavigateItems = filtered.length > 1;
  const canSlide = hasMultipleInCurrentItem || canNavigateItems;

  const handleNext = () => {
    if (!lightboxItem) return;
    if (!isCurrentItemVideo && currentSubPhotos.length > 1 && currentSlideIndex < currentSubPhotos.length - 1) {
      setCurrentSlideIndex((prev) => prev + 1);
    } else if (filtered.length > 1) {
      const nextIdx = (activeItemIndex + 1) % filtered.length;
      setLightboxItem(filtered[nextIdx]);
      setCurrentSlideIndex(0);
    }
  };

  const handlePrev = () => {
    if (!lightboxItem) return;
    if (!isCurrentItemVideo && currentSlideIndex > 0) {
      setCurrentSlideIndex((prev) => prev - 1);
    } else if (filtered.length > 1) {
      const prevIdx = (activeItemIndex - 1 + filtered.length) % filtered.length;
      const prevItem = filtered[prevIdx];
      const prevIsVideo = isVideoItem(prevItem);
      const prevSubPhotos =
        !prevIsVideo && prevItem.type === 'photo' ? [prevItem.url, ...(prevItem.gallery || [])].filter(Boolean) : [];
      setLightboxItem(prevItem);
      setCurrentSlideIndex(prevSubPhotos.length > 0 ? prevSubPhotos.length - 1 : 0);
    }
  };

  // Keyboard navigation (Escape, Left Arrow, Right Arrow)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseLightbox();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };
    if (lightboxItem) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [lightboxItem, activeItemIndex, currentSlideIndex, filtered]);

  // Touch Swipe Handlers for Mobile & Tablet screens
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
    touchEndXRef.current = e.touches[0].clientX;
    touchEndYRef.current = e.touches[0].clientY;
    setTouchSwiping(true);
    setTouchDeltaX(0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndXRef.current = e.touches[0].clientX;
    touchEndYRef.current = e.touches[0].clientY;
    const deltaX = touchEndXRef.current - touchStartXRef.current;
    const deltaY = touchEndYRef.current - touchStartYRef.current;

    // Apply horizontal drag effect if horizontal movement is dominant
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 6) {
      setTouchDeltaX(deltaX);
    }
  };

  const handleTouchEnd = () => {
    setTouchSwiping(false);
    const deltaX = touchEndXRef.current - touchStartXRef.current;
    const deltaY = touchEndYRef.current - touchStartYRef.current;
    setTouchDeltaX(0);

    // If swipe distance is > 25px and dominant on X-axis (more forgiving on mobile thumbs)
    if (Math.abs(deltaX) > 25 && Math.abs(deltaX) > Math.abs(deltaY) * 0.7) {
      if (deltaX < 0) {
        // Swiped left -> Next slide/photo
        handleNext();
      } else {
        // Swiped right -> Previous slide/photo
        handlePrev();
      }
    }
  };

  return (
    <section id="gallery-video-section" className="py-12 md:py-16 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header & Filter Controls */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4 pb-4 border-b border-slate-200">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: primaryColor }} />
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{badge}</span>
            </div>
            <h2
              className={`text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight inline-flex items-center gap-2.5 ${
                !isPageMode ? 'cursor-pointer group hover:text-emerald-700 transition-colors' : ''
              }`}
              onClick={() => !isPageMode && navigate('gallery')}
            >
              <span>{title}</span>
              {!isPageMode && (
                <span
                  className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-emerald-50 text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white transition-all shadow-2xs group-hover:translate-x-1 shrink-0"
                  title="View Full Media & Video Archives"
                >
                  <ArrowRight className="w-4 h-4 sm:w-4.5 sm:h-4.5" />
                </span>
              )}
            </h2>
            {subtitle && <p className="text-sm text-slate-500 mt-1 max-w-2xl">{subtitle}</p>}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5 bg-slate-100 p-1.5 rounded-xl self-start md:self-auto border border-slate-200">
              <button
                id="gallery-filter-all"
                onClick={() => {
                  setActiveFilter('all');
                  setVisibleCount(limit || 8);
                }}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  activeFilter === 'all'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                All ({gallery.length})
              </button>
              <button
                id="gallery-filter-photo"
                onClick={() => {
                  setActiveFilter('photo');
                  setVisibleCount(limit || 8);
                }}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                  activeFilter === 'photo'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <ImageIcon className="w-3.5 h-3.5 text-emerald-600" />
                <span>Photos ({gallery.filter((g) => g.type === 'photo').length})</span>
              </button>
              <button
                id="gallery-filter-video"
                onClick={() => {
                  setActiveFilter('video');
                  setVisibleCount(limit || 8);
                }}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                  activeFilter === 'video'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Video className="w-3.5 h-3.5 text-rose-600" />
                <span>Videos ({gallery.filter(isVideoItem).length})</span>
              </button>
            </div>
          </div>
        </div>

        {/* Unified Gallery Grid: Combined Photos & Videos */}
        {visibleGallery.length === 0 ? (
          <div className="bg-white p-12 rounded-2xl border border-dashed border-slate-300 text-center max-w-md mx-auto shadow-xs">
            <ImageIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-semibold text-slate-600">Belum ada item galeri dalam kategori ini</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {visibleGallery.map((item) => (
              <GalleryGridCard
                key={item.id}
                item={item}
                primaryColor={primaryColor}
                onOpenLightbox={handleOpenLightbox}
                getYouTubeThumbnail={getYouTubeThumbnail}
              />
            ))}
          </div>
        )}

        {/* Bottom Actions - More button only in Page Mode */}
        {isPageMode && filtered.length > visibleCount && (
          <div className="mt-10 text-center">
            <button
              type="button"
              id="gallery-more-btn"
              onClick={() => setVisibleCount((prev) => prev + (limit || 8))}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-bold text-xs sm:text-sm shadow-md hover:shadow-lg hover:opacity-95 active:scale-98 transition-all cursor-pointer"
              style={{ backgroundColor: primaryColor }}
            >
              <ChevronDown className="w-4 h-4 animate-bounce" />
              <span>Show More ({filtered.length - visibleCount} Photos/Videos)</span>
            </button>
          </div>
        )}
      </div>

      {/* FULLSCREEN TOUCH-SWIPEABLE LIGHTBOX MODAL */}
      {lightboxItem && (
        <div
          id="gallery-lightbox-modal"
          onClick={handleCloseLightbox}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md p-2 sm:p-4 md:p-6 animate-in fade-in"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-slate-900 rounded-2xl sm:rounded-3xl overflow-hidden max-w-4xl w-full border border-slate-700 shadow-2xl relative flex flex-col max-h-[94vh] animate-in zoom-in-95 duration-150"
          >
            {/* Modal Header */}
            <div className="p-3 sm:p-4 flex items-center justify-between text-white border-b border-slate-800 bg-slate-900/95 shrink-0 gap-3">
              <div className="flex items-center gap-2 sm:gap-2.5 overflow-hidden">
                <span
                  className={`text-[10px] sm:text-xs font-bold uppercase px-2.5 py-1 rounded-md text-white shrink-0 shadow-xs flex items-center gap-1.5 ${
                    isVideoItem(lightboxItem) ? 'bg-red-600' : 'bg-emerald-600'
                  }`}
                >
                  {isVideoItem(lightboxItem) ? (
                    <>
                      <Film className="w-3.5 h-3.5" />
                      <span>
                        {isDirectVideoUrl(lightboxItem.url)
                          ? 'Video File'
                          : isIframeEmbed(lightboxItem.videoEmbed || lightboxItem.url)
                          ? 'Embed Video'
                          : getYouTubeId(lightboxItem.url)
                          ? 'YouTube Video'
                          : 'Video'}
                      </span>
                    </>
                  ) : (
                    <>
                      <ImageIcon className="w-3.5 h-3.5" />
                      <span>Photo Gallery</span>
                    </>
                  )}
                </span>

                {/* Photo & Album Index Indicator */}
                <div className="flex items-center gap-2 text-xs text-slate-300 font-medium truncate">
                  {!isCurrentItemVideo && lightboxItem.type === 'photo' && currentSubPhotos.length > 1 ? (
                    <span className="bg-slate-800 px-2.5 py-0.5 rounded-md text-emerald-400 font-mono font-bold text-[11px] border border-slate-700">
                      Photo {currentSlideIndex + 1} / {currentSubPhotos.length}
                    </span>
                  ) : (
                    <span className="text-slate-400 text-[11px]">
                      Item {activeItemIndex + 1} of {filtered.length}
                    </span>
                  )}
                </div>
              </div>

              <button
                id="close-lightbox-btn"
                onClick={handleCloseLightbox}
                aria-label="Close Gallery"
                className="px-3 py-1.5 text-xs sm:text-sm font-semibold text-slate-200 hover:text-white bg-slate-800 hover:bg-slate-700 active:scale-95 rounded-xl transition-all shrink-0 cursor-pointer shadow-xs border border-slate-700 flex items-center gap-1.5"
                title="Close (Esc)"
              >
                <X className="w-4 h-4" />
                <span className="hidden sm:inline">Close</span>
              </button>
            </div>

            {/* Modal Body / Touch-Enabled Swipe Media Canvas */}
            <div
              className="relative p-2 sm:p-4 bg-black flex-1 flex items-center justify-center min-h-[260px] sm:min-h-[380px] max-h-[64vh] overflow-hidden group select-none"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {isVideoItem(lightboxItem) ? (
                <div className="w-full aspect-video rounded-xl overflow-hidden shadow-inner max-h-[58vh] flex items-center justify-center bg-black">
                  {isDirectVideoUrl(lightboxItem.url) ? (
                    <video
                      src={lightboxItem.url}
                      controls
                      autoPlay
                      playsInline
                      className="w-full h-full object-contain max-h-[58vh]"
                    />
                  ) : isIframeEmbed(lightboxItem.videoEmbed || lightboxItem.url) ? (
                    <iframe
                      src={extractIframeSrc(lightboxItem.videoEmbed || lightboxItem.url) || lightboxItem.url}
                      title={lightboxItem.title}
                      className="w-full h-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : getYouTubeId(lightboxItem.url) ? (
                    <iframe
                      src={getYouTubeEmbedUrl(lightboxItem.url, true)}
                      title={lightboxItem.title}
                      className="w-full h-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : getVimeoId(lightboxItem.url) ? (
                    <iframe
                      src={`https://player.vimeo.com/video/${getVimeoId(lightboxItem.url)}?autoplay=1`}
                      title={lightboxItem.title}
                      className="w-full h-full border-0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <iframe
                      src={lightboxItem.url}
                      title={lightboxItem.title}
                      className="w-full h-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  )}
                </div>
              ) : (
                <div className="relative w-full h-full flex items-center justify-center">
                  <img
                    src={currentSubPhotos[currentSlideIndex] || lightboxItem.url}
                    alt={`${lightboxItem.title} - Photo ${currentSlideIndex + 1}`}
                    className="max-h-[56vh] sm:max-h-[58vh] w-auto max-w-full object-contain rounded-xl transition-transform duration-150 ease-out"
                    style={{
                      transform: touchSwiping && touchDeltaX !== 0 ? `translateX(${touchDeltaX * 0.4}px)` : 'translateX(0px)',
                    }}
                    draggable={false}
                  />

                  {/* Prominent Left Navigation Button */}
                  {canSlide && (
                    <button
                      id="lightbox-prev-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrev();
                      }}
                      className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-black/60 hover:bg-black/85 text-white flex items-center justify-center shadow-lg transition-all active:scale-90 cursor-pointer z-20 border border-white/20 hover:border-white/40"
                      aria-label="Previous Photo"
                      title="Previous (←)"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                  )}

                  {/* Prominent Right Navigation Button */}
                  {canSlide && (
                    <button
                      id="lightbox-next-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNext();
                      }}
                      className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-black/60 hover:bg-black/85 text-white flex items-center justify-center shadow-lg transition-all active:scale-90 cursor-pointer z-20 border border-white/20 hover:border-white/40"
                      aria-label="Next Photo"
                      title="Next (→)"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  )}

                  {/* Interactive Dots Indicator for Sub-Photos */}
                  {currentSubPhotos.length > 1 && (
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md z-20 border border-white/10">
                      {currentSubPhotos.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentSlideIndex(idx);
                          }}
                          className={`rounded-full transition-all cursor-pointer ${
                            currentSlideIndex === idx
                              ? 'w-4 h-2 bg-emerald-400'
                              : 'w-2 h-2 bg-white/50 hover:bg-white/80'
                          }`}
                          aria-label={`View photo ${idx + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer: Full Title & Caption */}
            <div className="p-3.5 sm:p-4 bg-slate-900 text-white border-t border-slate-800 shrink-0 overflow-y-auto max-h-36 sm:max-h-44">
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-bold text-sm sm:text-base md:text-lg text-slate-100 leading-snug break-words">
                  {lightboxItem.title}
                </h3>
                {canNavigateItems && (
                  <span className="text-[11px] text-slate-400 font-mono bg-slate-800 px-2 py-0.5 rounded-md border border-slate-700 shrink-0 hidden sm:inline-block">
                    {activeItemIndex + 1} / {filtered.length}
                  </span>
                )}
              </div>
              {lightboxItem.caption && (
                <p className="text-xs sm:text-sm text-slate-300 mt-1.5 leading-relaxed break-words">
                  {lightboxItem.caption}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

interface GalleryGridCardProps {
  item: GalleryItem;
  primaryColor: string;
  onOpenLightbox: (item: GalleryItem, initialIndex: number) => void;
  getYouTubeThumbnail: (url: string) => string | null;
}

function GalleryGridCard({
  item,
  primaryColor,
  onOpenLightbox,
  getYouTubeThumbnail,
}: GalleryGridCardProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const subPhotos = item.type === 'photo' ? [item.url, ...(item.gallery || [])].filter(Boolean) : [];
  const hasMultiPhotos = subPhotos.length > 1;

  // Touch Swipe Engine specifically for this card on Mobile
  const touchStartXRef = useRef<number>(0);
  const touchStartYRef = useRef<number>(0);
  const touchEndXRef = useRef<number>(0);
  const touchEndYRef = useRef<number>(0);
  const isSwipingRef = useRef<boolean>(false);
  const [touchDeltaX, setTouchDeltaX] = useState<number>(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!hasMultiPhotos) return;
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
    touchEndXRef.current = e.touches[0].clientX;
    touchEndYRef.current = e.touches[0].clientY;
    isSwipingRef.current = false;
    setTouchDeltaX(0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!hasMultiPhotos) return;
    touchEndXRef.current = e.touches[0].clientX;
    touchEndYRef.current = e.touches[0].clientY;
    const diffX = touchEndXRef.current - touchStartXRef.current;
    const diffY = touchEndYRef.current - touchStartYRef.current;

    // Detect horizontal swipe intent
    if (Math.abs(diffX) > 8 && Math.abs(diffX) > Math.abs(diffY)) {
      isSwipingRef.current = true;
      setTouchDeltaX(diffX);
    }
  };

  const handleTouchEnd = () => {
    if (!hasMultiPhotos) return;
    const diffX = touchEndXRef.current - touchStartXRef.current;
    const diffY = touchEndYRef.current - touchStartYRef.current;
    setTouchDeltaX(0);

    // If swipe passed threshold (> 25px) and was primarily horizontal
    if (isSwipingRef.current && Math.abs(diffX) > 25 && Math.abs(diffX) > Math.abs(diffY) * 0.7) {
      if (diffX < 0) {
        // Swiped left -> next photo
        setCurrentSlide((prev) => (prev + 1) % subPhotos.length);
      } else {
        // Swiped right -> previous photo
        setCurrentSlide((prev) => (prev - 1 + subPhotos.length) % subPhotos.length);
      }
      // Hold swiping flag briefly to prevent triggering card click
      setTimeout(() => {
        isSwipingRef.current = false;
      }, 150);
    } else {
      isSwipingRef.current = false;
    }
  };

  const handleCardClick = () => {
    if (isSwipingRef.current) return;
    onOpenLightbox(item, currentSlide);
  };

  const handlePrevSlide = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    setCurrentSlide((prev) => (prev - 1 + subPhotos.length) % subPhotos.length);
  };

  const handleNextSlide = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    setCurrentSlide((prev) => (prev + 1) % subPhotos.length);
  };

  const handleDotClick = (e: React.MouseEvent, idx: number) => {
    e.stopPropagation();
    setCurrentSlide(idx);
  };

  const isVideo = isVideoItem(item);
  const isDirectVideo = isDirectVideoUrl(item.url);
  const ytThumb = getYouTubeThumbnail(item.videoEmbed || item.url);

  return (
    <div
      id={`gallery-card-${item.id}`}
      onClick={handleCardClick}
      className="group relative bg-white rounded-2xl border border-slate-200/90 overflow-hidden shadow-xs hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col select-none"
    >
      {/* Media Box with direct Touch Gestures */}
      <div
        className="relative aspect-4/3 overflow-hidden bg-slate-950"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {isVideo ? (
          <>
            {isDirectVideo ? (
              <div className="relative w-full h-full bg-slate-950 flex items-center justify-center">
                <video
                  src={item.url}
                  muted
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-90 pointer-events-none"
                />
                <div className="absolute inset-0 bg-black/35 flex items-center justify-center group-hover:bg-black/20 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-red-600 text-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Play className="w-6 h-6 fill-current ml-0.5" />
                  </div>
                </div>
              </div>
            ) : (
              <>
                <img
                  src={
                    ytThumb ||
                    'https://images.unsplash.com/photo-1536240478700-b869070f9279?auto=format&fit=crop&w=800&q=80'
                  }
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-90"
                  loading="lazy"
                  draggable={false}
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/25 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-red-600 text-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Play className="w-6 h-6 fill-current ml-0.5" />
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <div className="relative w-full h-full overflow-hidden">
            <img
              key={currentSlide}
              src={subPhotos[currentSlide] || item.url}
              alt={`${item.title} - Foto ${currentSlide + 1}`}
              className="w-full h-full object-cover transition-transform duration-200"
              style={{
                transform: touchDeltaX !== 0 ? `translateX(${touchDeltaX * 0.3}px)` : 'translateX(0px)',
              }}
              loading="lazy"
              draggable={false}
            />
          </div>
        )}

        {/* Badge: Type Indicator (Left Top) */}
        <div className="absolute top-3 left-3 z-20 pointer-events-none">
          <span
            className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md text-white shadow-xs flex items-center gap-1.5 ${
              isVideo ? 'bg-red-600' : 'bg-slate-900/80 backdrop-blur-xs'
            }`}
          >
            {isVideo ? (
              <>
                <Film className="w-3.5 h-3.5" />
                <span>
                  {isDirectVideo
                    ? 'Video File'
                    : isIframeEmbed(item.videoEmbed || item.url)
                    ? 'Embed Video'
                    : 'YouTube Video'}
                </span>
              </>
            ) : (
              <>
                <ImageIcon className="w-3.5 h-3.5" />
                <span>Moments</span>
              </>
            )}
          </span>
        </div>

        {/* Badge: Multi-Photo Count Indicator (Right Top) */}
        {hasMultiPhotos && (
          <div className="absolute top-3 right-3 z-20 pointer-events-none">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-emerald-600/95 text-white shadow-xs backdrop-blur-xs flex items-center gap-1">
              <Layers className="w-3 h-3" />
              <span>
                Photo {currentSlide + 1}/{subPhotos.length}
              </span>
            </span>
          </div>
        )}

        {/* Multi-Photo Navigation Controls ON THE CARD (Mobile & Desktop) */}
        {hasMultiPhotos && (
          <>
            {/* Prev Button */}
            <button
              type="button"
              onClick={handlePrevSlide}
              aria-label="Previous Photo"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 hover:bg-black/90 active:scale-90 text-white flex items-center justify-center backdrop-blur-xs border border-white/20 transition-all z-20 cursor-pointer shadow-md"
              title="Previous"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Next Button */}
            <button
              type="button"
              onClick={handleNextSlide}
              aria-label="Next Photo"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 hover:bg-black/90 active:scale-90 text-white flex items-center justify-center backdrop-blur-xs border border-white/20 transition-all z-20 cursor-pointer shadow-md"
              title="Next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Pagination Dots at Bottom of Image */}
            <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 backdrop-blur-xs z-20 border border-white/10 pointer-events-auto">
              {subPhotos.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={(e) => handleDotClick(e, idx)}
                  aria-label={`View photo ${idx + 1}`}
                  className={`rounded-full transition-all cursor-pointer ${
                    currentSlide === idx
                      ? 'w-4 h-1.5 bg-emerald-400'
                      : 'w-1.5 h-1.5 bg-white/60 hover:bg-white'
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {/* Hover expand icon for single items */}
        {!hasMultiPhotos && (
          <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity bg-black/70 text-white p-1.5 rounded-lg z-10 pointer-events-none">
            <Maximize2 className="w-4 h-4" />
          </div>
        )}
      </div>

      {/* Card Content & Caption */}
      <div className="p-4 flex-1 flex flex-col justify-between">
        <div>
          <h3 className="font-bold text-xs sm:text-sm text-slate-800 line-clamp-2 leading-snug group-hover:text-emerald-700 transition-colors">
            {item.title}
          </h3>
          {item.caption && (
            <p className="text-[11px] text-slate-500 mt-1.5 line-clamp-2 leading-relaxed">
              {item.caption}
            </p>
          )}
        </div>

        {/* Slide info / action hint */}
        {hasMultiPhotos ? (
          <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-500 font-medium">
            <span className="flex items-center gap-1 text-emerald-600 font-semibold">
              <Layers className="w-3 h-3" />
              <span>
                Slide {currentSlide + 1} of {subPhotos.length}
              </span>
            </span>
            <span className="text-emerald-600 font-bold group-hover:translate-x-0.5 transition-transform">
              View →
            </span>
          </div>
        ) : (
          <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400">
            <span>{isVideo ? 'Play Video' : 'View Image'}</span>
            <span className="text-emerald-600 font-bold group-hover:translate-x-0.5 transition-transform">
              View →
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
